import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE from "./config";
import { DataSheetGrid, keyColumn, textColumn } from "react-datasheet-grid";
import "react-datasheet-grid/dist/style.css";
import "./PipelineConfig.css";

const PipelineConfig = () => {
  const { pipelineId } = useParams();
  const navigate = useNavigate();

  const [pipelineName, setPipelineName] = useState("");
  const [inputs, setInputs] = useState([]);
  const [numRows, setNumRows] = useState(10);
  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState([]);
  const [helpPopup, setHelpPopup] = useState(null);
  const [radioSelections, setRadioSelections] = useState({});
  const [paramValues, setParamValues] = useState({});
  const [igenomeOptions, setIgenomeOptions] = useState([]);
  const [paramConfigMap, setParamConfigMap] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pipelineRes = await axios.get(`${API_BASE}/nfcore-pipelines/${pipelineId}`);
        setPipelineName(pipelineRes.data.pipeline_name);

        const inputsRes = await axios.get(`${API_BASE}/nfcore-pipelines/inputs/${pipelineId}`);
        setInputs(inputsRes.data);

        const optionsRes = await axios.get(`${API_BASE}/nfcore-pipelines/options/${pipelineId}`);
        setOptions(optionsRes.data);

        const configRes = await axios.get(`${API_BASE}/nfcore-pipelines/configuration/${pipelineId}`);
        setParamConfigMap(configRes.data);
      } catch (err) {
        console.error("Error loading pipeline config:", err);
      }
    };

    fetchData();
  }, [pipelineId]);

  useEffect(() => {
    if (inputs.length === 0 || numRows <= 0) return;

    const newRows = Array.from({ length: numRows }, () => {
      const row = {};
      for (const col of inputs) {
        row[col.column_name] = "";
      }
      return row;
    });

    setRows(newRows);
  }, [inputs, numRows]);

  useEffect(() => {
    const fetchIgenomes = async () => {
      try {
        const res = await axios.get(`${API_BASE}/igenomes`, {
          params: { pipelineId }
        });
        setIgenomeOptions(res.data);
      } catch (err) {
        console.error("Failed to fetch igenomes:", err);
      }
    };

    if (pipelineId) {
      fetchIgenomes();
    }
  }, [pipelineId]);
    // Build editable columns for the grid
  const columns = inputs.map((col) =>
    keyColumn(col.column_name, {
      ...textColumn,
      title: (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <span style={{ color: col.required === "Y" ? "red" : undefined }}>
            {col.column_name}
            {col.required === "Y" && "*"}
          </span>
          <span title={col.column_description} style={{ cursor: "help", fontSize: "14px" }}>
            🛈
          </span>
        </span>
      ),
      renderWhenScrolling: false,
    })
  );

  const renderInput = (param, allParamsInSection = []) => {
    const id = `param-${param.parameter}`;
    const defaultVal = param.default ?? "";
    let listValues = param.list_values?.split(",").map((v) => v.trim()) || [];

    // Inject dynamic values from configuration table if applicable
    const configMatches = paramConfigMap.filter(
      (entry) => entry.dependent_parameter === param.parameter
    );

    for (const match of configMatches) {
      const controllingVal = paramValues[match.primary_parameter];
      if (controllingVal === match.primary_value) {
        listValues = match.dependent_value?.split(",").map((v) => v.trim()) || [];
        break;
      }
    }

    // Conditional visibility for dependent parameters
    if (param.dependent === "Y" && param.dependent_main) {
      const controllingParam = options.find((p) => p.id === param.dependent_main);
      const controllingKey = controllingParam?.parameter;
      const controllingVal = paramValues[controllingKey];
      if (!controllingVal) return null;
    }

    if (param.type === "radio") {
      return (
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
          {allParamsInSection.map((radioParam) => (
            <label
              key={radioParam.parameter}
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
              title={radioParam.description || ""}
            >
              <input
                type="radio"
                name={`radio-${radioParam.section}`}
                checked={radioSelections[radioParam.section] === radioParam.parameter}
                onChange={() => {
                  setRadioSelections((prev) => ({
                    ...prev,
                    [radioParam.section]: radioParam.parameter,
                  }));

                  setParamValues((prev) => {
                    const updated = { ...prev };
                    allParamsInSection.forEach((p) => {
                      updated[p.parameter] = false;
                    });
                    updated[radioParam.parameter] = true;
                    return updated;
                  });
                }}
              />
              {radioParam.parameter.replace(/_/g, " ")}
            </label>
          ))}
        </div>
      );
    }

    const commonProps = {
      id,
      defaultValue: defaultVal,
      style: { padding: "6px", fontSize: "14px" },
      onChange: (e) => {
        const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setParamValues((prev) => ({ ...prev, [param.parameter]: val }));
      },
    };

    switch (param.type) {
      case "boolean":
        return <input type="checkbox" defaultChecked={defaultVal === "true"} {...commonProps} />;
      case "integer":
      case "number":
        return <input type="number" {...commonProps} />;
      case "select":
        return (
          <select {...commonProps}>
            <option value="">-- Select --</option>
            {listValues.map((val) => (
              <option key={val} value={val}>
                {val}
              </option>
            ))}
          </select>
        );
      case "igenome":
        return (
          <select {...commonProps}>
            <option value="">-- Select iGenome --</option>
            {igenomeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      case "multiselect":
        return (
          <select multiple {...commonProps} size={Math.min(6, listValues.length)}>
            {listValues.map((val) => (
              <option key={val} value={val}>
                {val}
              </option>
            ))}
          </select>
        );
      default:
        return <input type="text" {...commonProps} />;
    }
  };
    const groupedOptions = options.reduce((acc, opt) => {
    if (!acc[opt.section]) acc[opt.section] = [];
    acc[opt.section].push(opt);
    return acc;
  }, {});

  const validateRows = (rows, schema) => {
    const errors = [];
    for (const col of schema) {
      if (col.required === "Y") {
        rows.forEach((row, i) => {
          if (!row[col.column_name]) {
            errors.push(`Row ${i + 1}: ${col.column_name} is required`);
          }
        });
      }
      if (col.unique === "Y") {
        const values = rows.map((row) => row[col.column_name]);
        const duplicates = values.filter((v, i, arr) => arr.indexOf(v) !== i && v);
        if (duplicates.length > 0) {
          errors.push(`${col.column_name} must be unique`);
        }
      }
    }
    return errors;
  };

  const handleLaunchClick = useCallback(() => {
    const errors = validateRows(rows, inputs);
    if (errors.length > 0) {
      alert("SampleSheet validation failed:\n" + errors.join("\n"));
      return;
    }

    const selectedParams = {};
    const radiosBySection = Object.fromEntries(
      Object.entries(groupedOptions).map(([section, params]) => [
        section,
        params.filter((p) => p.type === "radio"),
      ])
    );

    Object.entries(radiosBySection).forEach(([section, radios]) => {
      const chosenParam = radioSelections[section];
      if (chosenParam) {
        selectedParams[chosenParam] = "true";
      }
    });

    for (const param of options) {
      if (param.type === "radio") continue;

      if (param.dependent === "Y" && param.dependent_main) {
        const controllingParam = options.find((p) => p.id === param.dependent_main);
        const controllingKey = controllingParam?.parameter;
        const controllingVal = paramValues[controllingKey];
        if (!controllingVal) continue;
      }

      const key = param.parameter;
      const type = param.type;
      const defaultVal = param.default;
      const inputEl = document.getElementById(`param-${key}`);
      if (!inputEl) continue;

      if (type === "multiselect") {
        const selected = Array.from(inputEl.selectedOptions).map((opt) => opt.value);
        if (selected.length > 0) selectedParams[key] = selected;
      } else if (type === "select") {
        if (inputEl.value) selectedParams[key] = inputEl.value;
      } else if (type === "boolean") {
        const val = inputEl.checked.toString();
        if (val !== (defaultVal ?? "").toString()) selectedParams[key] = val;
      } else {
        const val = inputEl.value;
        if (val && val !== defaultVal) {
          selectedParams[key] = val;
        }
      }
    }

    navigate(`/launch/${pipelineId}`, {
      state: { pipelineId, pipelineName, sampleSheet: rows, selectedParams },
    });
  }, [pipelineId, rows, inputs, options, navigate, groupedOptions, radioSelections, paramValues]);

  return (
    <div style={{ padding: "20px" }}>
      <h2>
        Configure parameters and inputs for <strong>{pipelineName || "..."} pipeline run</strong>
      </h2>

      <div style={{ marginTop: "30px" }}>
        <h3>1. Create SampleSheet</h3>
        <label>
          <strong>Add/Delete rows in the sheet per your sample count</strong>
          <br />
          Number of rows:{" "}
          <select value={numRows} onChange={(e) => setNumRows(parseInt(e.target.value, 10))}>
            {[10, 20, 30, 50, 100].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>

        {columns.length > 0 && rows.length > 0 && (
          <div style={{ marginTop: "20px", height: "500px", overflow: "auto" }}>
            <DataSheetGrid
              value={rows}
              onChange={setRows}
              columns={columns}
              headerRowHeight={40}
              freezeFirstColumn={true}
            />
          </div>
        )}
      </div>

      <div style={{ marginTop: "40px" }}>
        <h3>2. Configure Pipeline Parameters</h3>
        {options.length === 0 ? (
          <p>Loading pipeline parameters...</p>
        ) : (
          Object.entries(groupedOptions).map(([sectionName, params]) => {
            const radioParams = params.filter((p) => p.type === "radio");
            const nonRadioParams = params.filter((p) => p.type !== "radio");

            return (
              <div key={sectionName} style={{ marginBottom: "40px" }}>
                <h4 style={{
                  background: "#f0f0f0",
                  padding: "10px 12px",
                  marginBottom: "10px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  textTransform: "capitalize",
                }}>
                  {sectionName.replace(/_/g, " ")}
                </h4>

                {radioParams.length > 0 && (
                  <div style={{ marginBottom: "16px" }}>
                    <label style={{ fontWeight: "bold", marginRight: "8px" }}>
                      {sectionName.replace(/_/g, " ")} selection:
                    </label>
                    {renderInput(radioParams[0], radioParams)}
                  </div>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "20px" }}>
                  {nonRadioParams.map((param) => {
                    if (param.dependent === "Y" && param.dependent_main) {
                      const controllingParam = options.find((p) => p.id === param.dependent_main);
                      const controllingKey = controllingParam?.parameter;
                      const controllingVal = paramValues[controllingKey];
                      if (!controllingVal) return null;
                    }

                    return (
                      <div key={param.parameter} style={{ display: "flex", flexDirection: "column" }}>
                        <label style={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: "6px" }}>
                          {param.parameter.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())}
                          <span title={param.description} style={{ cursor: "help" }}>🛈</span>
                          {param.help_text && (
                            <span style={{ cursor: "pointer" }} onClick={() => setHelpPopup(param.help_text)}>
                              ❔
                            </span>
                          )}
                        </label>
                        {renderInput(param)}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>

      {helpPopup && (
        <div style={{
          position: "fixed",
          top: "20%",
          left: "50%",
          transform: "translateX(-50%)",
          background: "#fff",
          border: "1px solid #ccc",
          padding: "20px",
          zIndex: 1000,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          maxWidth: "640px",
        }}>
          <p style={{ whiteSpace: "pre-wrap", marginBottom: "16px" }}>{helpPopup}</p>
          <button onClick={() => setHelpPopup(null)}>Close</button>
        </div>
      )}

      <div style={{ marginTop: "40px", textAlign: "center" }}>
        <button style={{
          padding: "12px 24px",
          fontSize: "16px",
          backgroundColor: "#007bff",
          color: "#fff",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
        }} onClick={handleLaunchClick}>
          NEXT
        </button>
      </div>
    </div>
  );
};

export default PipelineConfig;